import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Library from '../../Library'
import LinearLoader from '../../components/Loaders/LinearLoader'
import Footer from '../../components/Footer'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import ListScrollAllSides from '../../components/ListScrollAllSides'
import SearchInput from '../../components/Inputs/SearchInput'
import Button from '../../components/Buttons/Button'
import ModalContext from '../../context/ModalContext'
import { AddressMap } from '../../components/AddressMap'
import MapIcon from '../../components/Icons/MapIcon'
import VirtualizedList from '../../components/VirtualizedLists/VirtualizedList'
import useTranslate from '../../hooks/useTranslate'

export const Main = () => {
  const location = useLocation()
  const pageName = location.pathname.substring(1)
  const translate = useTranslate()
  const { showModal, setModalContent, setModalPadding } = useContext(ModalContext)
  const router = useNavigate()
  const dispatch = useDispatch()
  const isDataLoaded = useSelector((state) => state.loading.isDataLoaded)
  const virtualizedListRefs = {
    listRef: useRef(),
    headerRef: useRef()
  }
  const mainState = useSelector(state => state.main)
  const pageState = useSelector(state => state.page[pageName])
  const [filterQuery, setFilterQuery] = useState(pageState.filterQuery)

  useEffect(() => {
    setFilterQuery(pageState.filterQuery)
  }, [pageState.filterQuery])

  const headers = useMemo(() => [
    { id: 'id', name: 'id', width: 275, hidden: true },
    { id: 'timestamp', name: 'timestamp', dataType: 'date', type: 'timestamp', width: 165, hidden: true },
    { id: 'number', name: '№', dataType: 'number', width: 70, type: 'increment' },
    { id: 'status', name: 'Статус', type: 'status', width: 60 },
    { id: 'name', name: 'Название', width: 370 },
    { id: 'address', name: 'Адрес', width: 150 },
    { id: 'mailingAddress', name: 'Почтовый адрес', width: 150 },
    { id: 'mapAddress', name: 'Адрес на карте', width: 150 },
    { id: 'paymentDelay', name: 'Кол-во дней отсрочки', width: 70 },
    { id: 'atiCode', name: 'Код АТИ', width: 70 },
    { id: 'contacts', name: 'Контакты', width: 400 },
    { id: 'details', name: 'Реквизиты', width: 100 },
    { id: 'comment', name: 'Комментарий', width: 355 },
    { id: 'lastModifiedBy', name: 'Кто изменил', width: 200 },
    { id: 'lastModified', name: 'Дата изменения', dataType: 'date', type: 'timestamp', width: 140 },
  ].map(header => {
    if (!header.width) {
      header.width = 100
    }
    if (!header.dataType) {
      header.dataType = 'string'
    }
    return header
  }), [])

  const searchQueryHandler = useCallback((e) => {
    if (!filterQuery) {
      return
    }
    const newFilterQuery = {
      ...filterQuery,
      [e.target.name]: e.target.value,
    }
    setFilterQuery(newFilterQuery)
    dispatch({ type: 'SET_FILTER_QUERY', page: pageName, payload: newFilterQuery })
  }, [filterQuery, dispatch, pageName])

  const { customers, loading, error } = useSelector(state => state.data)

  const filteredTableData = useMemo(() => {
    const searchQuery = filterQuery.search.toLocaleLowerCase()

    const filtered = customers.filter((item) =>
      Object.values(item)
        .toString()
        .toLocaleLowerCase()
        .includes(searchQuery)
    )

    pageState.sort.key && Library.sort(filtered, pageState.sort)

    return filtered
  }, [customers, filterQuery.search, pageState.sort])

  useEffect(() => {
    Library.activeTabScroll(mainState)
  }, [customers, mainState])

  //======NEW ENTRY======//
  const newEntry = useCallback(() => {
    router(`/${pageName}/new`)
  }, [router, pageName])

  return (
    <>
      <LinearLoader
        isLoading={loading || !isDataLoaded}
        style={{ height: '4px' }}
      />
      {isDataLoaded && !loading && !error &&
        <>
          <VirtualizedList
            ref={virtualizedListRefs}
            offsetHeight={140}
            headers={headers}
            pageName={pageName}
            items={filteredTableData}
            filterQuery={filterQuery}
            navigateTo={pageName}
            zebraStriped={true}
            labelConfig={{ labelKey: 'name', labelFormat: 'string' }}
          />
          <Footer>
            <ListScrollAllSides virtualizedListRefs={virtualizedListRefs}/>
            <Button
              onClick={newEntry}
              title={translate('newEntry')}
            >
              {translate('newEntry')}
            </Button>
            {/* ------- ADDRESS MAP ------- */}
            <Button
              style={{ minWidth: '50px', maxWidth: '50px', padding: '0 14.5px' }}
              onClick={(e) => {
                e.preventDefault()
                setModalContent(
                  <AddressMap
                    data={customers}
                    addressKey='mapAddress'
                    latitudeKey='mapAddressLatitude'
                    longitudeKey='mapAddressLongitude'
                    popupInfo={[
                      { id: 'name', name: 'Название', type: 'text' },
                      { id: 'address', name: 'Адрес', type: 'text' },
                      { id: 'mailingAddress', name: 'Почтовый адрес', type: 'text' },
                      { id: 'mapAddress', name: 'Адрес на карте', type: 'text' },
                      { id: 'paymentDelay', name: 'Кол-во дней отсрочки', type: 'number' },
                      { id: 'atiCode', name: 'Код АТИ', type: 'text' },
                      { id: 'contacts', name: 'Контакты', type: 'text' },
                      { id: 'details', name: 'Реквизиты', type: 'text' },
                      { id: 'comment', name: 'Комментарий', type: 'text' },
                    ]}
                    clusterDisplayKey='name'
                  />)
                setModalPadding({ smallScreen: '30px 5px 20px 5px;', largeScreen: '25px' })
                showModal()
              }}
              title='Открыть карту'
            >
              <div style={{ display: 'flex', alignItems: 'center', width: '24px' }}>
                <MapIcon />
              </div>
            </Button>
            <SearchInput
              style={{ margin: '0 0 0 20px' }}
              label='Поиск'
              id='search'
              name='search'
              color='#FFF'
              borderColor='#FFF'
              focusedColor='#ffeb3b'
              placeholderColor='#FFF'
              borderOption={3}
              value={filterQuery.search}
              onChange={searchQueryHandler}
              debounce={500}
            />
          </Footer>
        </>
      }
    </>
  )
}